// src/components/EmployeeView/MailRequest.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import './MailRequest.css';
import DatePicker from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/red.css";
import { format } from 'date-fns';
import "react-multi-date-picker/styles/colors/teal.css";
import LeaveRequestsModal from "./LeaveRequestModal";
import {
  Card,
  Form,
  Button,
  Modal,
  Badge,
  InputGroup,
  Row,
  Col,
  Spinner,
  Alert
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaTimes,
  FaEdit,
  FaSave,
  FaUndo,
  FaPlus,
  FaEnvelope,
  FaHistory,
  FaCalendarAlt,
  FaUserTag,
  FaClipboardList,
  FaUser,
  FaCopy,
  FaCalendar,
  FaFileAlt,
  FaClock,
  FaInfoCircle,
  FaMinus,
  FaHome,
  FaDollarSign,
  FaTrash,
  FaMapMarkerAlt,
  FaRoute,
  FaChevronLeft,
  FaChevronRight
} from "react-icons/fa";
import { io } from "socket.io-client";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || API_URL;

const MailRequest = () => {
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const employeeId = storedUser.employeeId;
  const isAdmin = storedUser.role === 4;

  // State variables
  const [leaveRequestModal, showLeaveRequestModal] = useState(false);
  const [leaveData, setLeaveData] = useState({});
  const [templates, setTemplates] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newBody, setNewBody] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [emailDate] = useState(new Date().toISOString().split("T")[0]);
  const [wfhRecords, setWfhRecords] = useState([]);

  // Leave form state
  const [leaveForm, setLeaveForm] = useState({
    leaveType: "",
    leaveDate: [emailDate],
    to: "",
    ccList: ["tushar.mahadik@protovec.com", "kalpesh.urunkar@protovec.com"],
    subject: "",
    customSubject: "",
    body: "",
    compOffDate: "",
    duration: "",
    workedDate: "",
    wfhDates: []
  });

  // Date picker state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);

  // Leave records state for Planned/Unplanned leaves
  const [leaveRecords, setLeaveRecords] = useState([]);
  // Comp Off records state - array of objects for worked dates with their comp off dates
  const [compOffRecords, setCompOffRecords] = useState([]);
  // Day info fetched from API for each comp-off record index { suggested, isTravel, location, work_hour, hasWorked }
  const [compOffDayInfo, setCompOffDayInfo] = useState({});
  // Comp Off Cash records state
  const [cashOffRecords, setCashOffRecords] = useState([]);
  const [cashOffDayInfo, setCashOffDayInfo] = useState({});
  // Travel Site Visit records state
  const [travelRecords, setTravelRecords] = useState([]);

  const [newCc, setNewCc] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [editingBody, setEditingBody] = useState(false);
  const [editBody, setEditBody] = useState("");
  const [editingSubject, setEditingSubject] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [myReqs, setMyReqs] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [errorReqs, setErrorReqs] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const socketRef = useRef(null);

  // Toast notification
  const showCustomToast = useCallback((msg) => {
    setToastMsg(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }, []);

  // Check if comp off date is within 60 days of worked date
  const isWithin60Days = useCallback((workedDateStr, compOffDateStr) => {
    if (!workedDateStr || !compOffDateStr) return false;
    const worked = new Date(workedDateStr);
    const compOff = new Date(compOffDateStr);
    const days = (a, b) => (a - b) / (1000 * 60 * 60 * 24);
    const diffWorkToComp = days(compOff, worked);
    return diffWorkToComp >= 0 && diffWorkToComp <= 60;
  }, []);

  // Load templates
  const loadTemplates = useCallback(() => {
    fetch(`${API_URL}/api/subject-templates`)
      .then((res) => res.json())
      .then(setTemplates)
      .catch(() => showCustomToast("Failed to load templates"));
  }, [API_URL, showCustomToast]);

  // Load my requests
  const loadMyRequests = useCallback(() => {
    setLoadingReqs(true);
    setErrorReqs("");
    fetch(`${API_URL}/api/my-requests?employee_id=${employeeId}`)
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => {
        setMyReqs(data || []);
        setCurrentPage(1);
      })
      .catch(() => setErrorReqs("Could not load your requests"))
      .finally(() => setLoadingReqs(false));
  }, [API_URL, employeeId]);

  // Handle form change
  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === "leaveType") {
      // Reset all records when leave type changes
      if (value !== "Planned Leave" && value !== "Unplanned Leave" &&
        value !== "Work From Home Request" &&
        !value.includes("Compensatory Off") &&
        value !== "Travel (Site Visit)") {
        setLeaveRecords([]);
        setWfhRecords([]);
        setCompOffRecords([]);
        setTravelRecords([]);
        setSelectedDates([]);
      } else {
        // Update existing records with new leave type
        if (value === "Planned Leave" || value === "Unplanned Leave") {
          // If switching from other types to Leave, clear other records
          setWfhRecords([]);
          setCompOffRecords([]);
          setCashOffRecords([]);
          setCashOffDayInfo({});
          setTravelRecords([]);
          if (leaveRecords.length > 0) {
            const newLeaveType = value === "Planned Leave" ? "Planned" : "Unplanned";
            setLeaveRecords(prev =>
              prev.map(record => ({
                ...record,
                leaveType: newLeaveType
              }))
            );
          }
        } else if (value === "Work From Home Request") {
          // If switching to WFH, clear other records
          setLeaveRecords([]);
          setCompOffRecords([]);
          setCashOffRecords([]);
          setCashOffDayInfo({});
          setTravelRecords([]);
        } else if (value.includes("Compensatory Off")) {
          // If switching to Comp Off, clear other records
          setLeaveRecords([]);
          setWfhRecords([]);
          setTravelRecords([]);
          setSelectedDates([]);
          if (value === "Compensatory Off (Cash)") {
            setCompOffRecords([]);
          } else {
            setCashOffRecords([]);
            setCashOffDayInfo({});
          }
        } else if (value === "Travel (Site Visit)") {
          // If switching to Travel, clear other records
          setLeaveRecords([]);
          setWfhRecords([]);
          setCompOffRecords([]);
          setCashOffRecords([]);
          setCashOffDayInfo({});
          setSelectedDates([]);
        }
      }
    }

    setLeaveForm((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "body") {
        const count = value.trim().split(/\s+/).filter(Boolean).length;
        if (count > 500) return prev;
      }

      if (name === "subject" && value !== "__custom") {
        updated.customSubject = "";
      }

      if (name === "subject") {
        const match = templates.find((t) => t.subject === value);
        if (match) {
          updated.body = match.body;
          setEditingBody(false);
          setEditBody(match.body);
        }
      }

      return updated;
    });
  }, [templates, leaveRecords, wfhRecords, compOffRecords]);

  // Handle date selection for leaves/WFH
  const handleDateSelection = useCallback((dates) => {
    const dateArray = Array.isArray(dates) ? dates : [dates];
    const newSelectedDates = dateArray.map(date =>
      date instanceof Date ? date : new Date(date)
    );

    setSelectedDates(newSelectedDates);

    if (newSelectedDates.length > 0) {
      const formattedDates = newSelectedDates.map(date => format(date, "yyyy-MM-dd"));

      if (leaveForm.leaveType === "Work From Home Request") {
        // Create WFH records
        const newWfhRecords = formattedDates.map(date => ({
          wfhDate: date,
          wfhDay: "Full"
        }));
        setWfhRecords(newWfhRecords);
      } else if (leaveForm.leaveType === "Planned Leave" || leaveForm.leaveType === "Unplanned Leave") {
        // Create Leave records
        const newLeaveRecords = formattedDates.map(date => {
          let leaveType = "Planned";
          if (leaveForm.leaveType === "Unplanned Leave") {
            leaveType = "Unplanned";
          }
          return {
            leaveDate: date,
            leaveType: leaveType,
            leaveDay: "Full"
          };
        });
        setLeaveRecords(newLeaveRecords);
      }
    } else {
      if (leaveForm.leaveType === "Work From Home Request") {
        setWfhRecords([]);
      } else if (leaveForm.leaveType === "Planned Leave" || leaveForm.leaveType === "Unplanned Leave") {
        setLeaveRecords([]);
      }
    }
  }, [leaveForm.leaveType]);

  // Fetch day info (travel + attendance) for a comp-off worked date
  const fetchCompOffDayInfo = useCallback(async (index, date) => {
    if (!date || !employeeId) return;
    try {
      const res = await axios.get(`${API_URL}/api/reports/comp-off-day-info`, {
        params: { date, employeeId }
      });
      setCompOffDayInfo(prev => ({ ...prev, [index]: res.data }));
      // Auto-suggest duration if not manually set yet
      if (res.data.suggested) {
        setCompOffRecords(prev => {
          const updated = [...prev];
          if (!updated[index].durationManual) {
            updated[index] = { ...updated[index], duration: res.data.suggested };
          }
          return updated;
        });
      }
    } catch {
      setCompOffDayInfo(prev => ({ ...prev, [index]: null }));
    }
  }, [employeeId, API_URL]);
console.log('comp off day info', compOffDayInfo)
  // Fetch day info for a comp-off cash worked date
  const fetchCashOffDayInfo = useCallback(async (index, date) => {
    if (!date || !employeeId) return;
    try {
      const res = await axios.get(`${API_URL}/api/reports/comp-off-day-info`, {
        params: { date, employeeId }
      });
      setCashOffDayInfo(prev => ({ ...prev, [index]: res.data }));
      if (res.data.suggested) {
        setCashOffRecords(prev => {
          const updated = [...prev];
          if (!updated[index].durationManual) {
            updated[index] = { ...updated[index], duration: res.data.suggested };
          }
          return updated;
        });
      }
    } catch {
      setCashOffDayInfo(prev => ({ ...prev, [index]: null }));
    }
  }, [employeeId, API_URL]);

  // Add a new Comp Off Cash record
  const addCashOffRecord = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setCashOffRecords(prev => {
      const newIndex = prev.length;
      fetchCashOffDayInfo(newIndex, today);
      return [...prev, { workedDate: today, duration: "", error: "", durationManual: false }];
    });
  }, [fetchCashOffDayInfo]);

  // Update Comp Off Cash record
  const updateCashOffRecord = useCallback((index, field, value) => {
    if (field === 'workedDate') fetchCashOffDayInfo(index, value);
    setCashOffRecords(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        ...(field === 'duration' ? { durationManual: true } : {})
      };
      return updated;
    });
  }, [fetchCashOffDayInfo]);

  // Remove a Comp Off Cash record
  const removeCashOffRecord = useCallback((index) => {
    setCashOffRecords(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  // Add a new Comp Off record
  const addCompOffRecord = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setCompOffRecords(prev => {
      const newIndex = prev.length;
      fetchCompOffDayInfo(newIndex, today);
      return [...prev, { workedDate: today, duration: "", compOffDate: "", error: "", durationManual: false }];
    });
  }, [fetchCompOffDayInfo]);

  // Add a new Travel Site Visit record
  const addTravelRecord = useCallback(() => {
    const now = new Date().toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
    setTravelRecords(prev => [
      ...prev,
      {
        location: "",
        fromDateTime: now,
        toDateTime: now,
        error: ""
      }
    ]);
  }, []);

  // Update Comp Off record
  const updateCompOffRecord = useCallback((index, field, value) => {
    if (field === 'workedDate') fetchCompOffDayInfo(index, value);
    setCompOffRecords(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        ...(field === 'duration' ? { durationManual: true } : {})
      };

      // Validate comp off date when workedDate or compOffDate changes
      if (field === 'workedDate' || field === 'compOffDate') {
        const workedDate = field === 'workedDate' ? value : updated[index].workedDate;
        const compOffDate = field === 'compOffDate' ? value : updated[index].compOffDate;

        if (workedDate && compOffDate) {
          if (workedDate === compOffDate) {
            updated[index].error = "Worked date should not be same as comp off date";
          } else if (!isWithin60Days(workedDate, compOffDate)) {
            updated[index].error = "Comp Off must be within 60 days of worked date";
          } else {
            updated[index].error = "";
          }
        } else {
          updated[index].error = "";
        }
      }

      return updated;
    });
  }, [isWithin60Days]);

  // Update Travel Site Visit record
  const updateTravelRecord = useCallback((index, field, value) => {
    setTravelRecords(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (field === 'fromDateTime' || field === 'toDateTime') {
        const from = field === 'fromDateTime' ? value : updated[index].fromDateTime;
        const to = field === 'toDateTime' ? value : updated[index].toDateTime;

        if (from && to) {
          updated[index].error = new Date(to) < new Date(from)
            ? "To date/time cannot be earlier than from date/time"
            : "";
        } else {
          updated[index].error = "";
        }
      }

      return updated;
    });
  }, []);

  // Remove a Comp Off record
  const removeCompOffRecord = useCallback((index) => {
    setCompOffRecords(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  // Remove a Travel Site Visit record
  const removeTravelRecord = useCallback((index) => {
    setTravelRecords(prev => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  // Handle WFH record change
  const handleWfhRecordChange = useCallback((index, field, value) => {
    setWfhRecords(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  // Remove a WFH record
  const removeWfhRecord = useCallback((index) => {
    setWfhRecords(prev => {
      const updated = [...prev];
      const removedDate = prev[index].wfhDate;
      updated.splice(index, 1);

      setSelectedDates(prevDates =>
        prevDates.filter(date => format(date, "yyyy-MM-dd") !== removedDate)
      );

      return updated;
    });
  }, []);

  // Calculate total WFH days
  const calculateTotalWfhDays = (records) => {
    const total = records.reduce((total, rec) => {
      if (!rec?.wfhDay) return total + 1;
      const dayValue = rec.wfhDay.toLowerCase() === "half" ? 0.5 : 1;
      return total + dayValue;
    }, 0);
    return total;
  };

  // Calculate total Comp Off days
  const calculateTotalCompOffDays = (records) => {
    return records.reduce((total, record) => {
      if (!record.duration) return total;
      const dayValue = record.duration.toLowerCase() === "half day" ? 0.5 : 1;
      return total + dayValue;
    }, 0);
  };

  const calculateTotalCashOffDays = (records) => {
    return records.reduce((total, record) => {
      if (!record.duration) return total;
      const dayValue = record.duration.toLowerCase() === "half day" ? 0.5 : 1;
      return total + dayValue;
    }, 0);
  };

  // Calculate total Travel days
  const calculateTotalTravelDays = (records) => {
    return records.reduce((total, record) => {
      if (!record.fromDateTime || !record.toDateTime) return total;
      const from = new Date(record.fromDateTime);
      const to = new Date(record.toDateTime);
      const diffTime = Math.abs(to - from);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
      return total + diffDays;
    }, 0);
  };

  // Handle leave record change
  const handleLeaveRecordChange = useCallback((index, field, value) => {
    setLeaveRecords(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  // Remove a leave record
  const removeLeaveRecord = useCallback((index) => {
    setLeaveRecords(prev => {
      const updated = [...prev];
      const removedDate = prev[index].leaveDate;
      updated.splice(index, 1);

      setSelectedDates(prevDates =>
        prevDates.filter(date => format(date, "yyyy-MM-dd") !== removedDate)
      );

      return updated;
    });
  }, []);

  const handleAddCc = useCallback((e) => {
    e.preventDefault();
    const email = newCc.trim();
    if (
      email &&
      !leaveForm.ccList.includes(email) &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      setLeaveForm((p) => ({ ...p, ccList: [...p.ccList, email] }));
      showCustomToast(`CC added: ${email}`);
    }
    setNewCc("");
  }, [leaveForm.ccList, newCc, showCustomToast]);

  const handleRemoveCc = useCallback((email) => {
    setLeaveForm((p) => ({
      ...p,
      ccList: p.ccList.filter((c) => c !== email),
    }));
    showCustomToast(`CC removed: ${email}`);
  }, [leaveForm.ccList, showCustomToast]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (!form.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Validation for Planned/Unplanned Leave
    if (leaveForm.leaveType === "Planned Leave" || leaveForm.leaveType === "Unplanned Leave") {
      if (leaveRecords.length === 0) {
        showCustomToast("Please select at least one date for leave.");
        setValidated(true);
        return;
      }

      const hasInvalidRecords = leaveRecords.some(record =>
        !record.leaveDate || !record.leaveType || !record.leaveDay
      );

      if (hasInvalidRecords) {
        showCustomToast("Please fill all details for selected leave dates.");
        setValidated(true);
        return;
      }
    }

    // Validation for Work From Home Request
    if (leaveForm.leaveType === "Work From Home Request") {
      if (wfhRecords.length === 0) {
        showCustomToast("Please select at least one date for Work From Home.");
        setValidated(true);
        return;
      }

      const hasInvalidWfhRecords = wfhRecords.some(record =>
        !record.wfhDate || !record.wfhDay
      );

      if (hasInvalidWfhRecords) {
        showCustomToast("Please fill all details for selected WFH dates.");
        setValidated(true);
        return;
      }
    }

    // Validation for Compensatory Off (Leave)
    if (leaveForm.leaveType === "Compensatory Off (Leave)") {
      if (compOffRecords.length === 0) {
        showCustomToast("Please add at least one comp off record.");
        setValidated(true);
        return;
      }

      // Validate all fields are filled
      const hasInvalidRecords = compOffRecords.some(record =>
        !record.workedDate || !record.duration || !record.compOffDate
      );

      if (hasInvalidRecords) {
        showCustomToast("Please fill all details for comp off records.");
        setValidated(true);
        return;
      }

      // Validate no errors in comp off records
      const hasErrors = compOffRecords.some(record => record.error);

      if (hasErrors) {
        showCustomToast("Please fix errors in comp off records.");
        setValidated(true);
        return;
      }
    }

    // Validation for Compensatory Off (Cash)
    if (leaveForm.leaveType === "Compensatory Off (Cash)") {
      if (cashOffRecords.length === 0) {
        showCustomToast("Please add at least one worked date record.");
        setValidated(true);
        return;
      }
      const hasInvalidCash = cashOffRecords.some(r => !r.workedDate || !r.duration);
      if (hasInvalidCash) {
        showCustomToast("Please fill all details for comp off cash records.");
        setValidated(true);
        return;
      }
    }

    // Validation for Travel (Site Visit)
    if (leaveForm.leaveType === "Travel (Site Visit)") {
      if (travelRecords.length === 0) {
        showCustomToast("Please add at least one travel record.");
        setValidated(true);
        return;
      }

      // Validate all fields are filled
      const hasInvalidRecords = travelRecords.some(record =>
        !record.location || !record.fromDateTime || !record.toDateTime
      );

      if (hasInvalidRecords) {
        showCustomToast("Please fill all details for travel records.");
        setValidated(true);
        return;
      }

      // Validate no errors in travel records
      const hasErrors = travelRecords.some(record => record.error);

      if (hasErrors) {
        showCustomToast("Please fix errors in travel records.");
        setValidated(true);
        return;
      }
    }

    setValidated(false);
    setShowConfirmModal(true);
  }, [leaveForm, leaveRecords, wfhRecords, compOffRecords, travelRecords, showCustomToast]);

  const confirmSend = useCallback(async () => {
    if (isSending) return;
    setIsSending(true);

    // Prepare payload based on leave type
    const payload = {
      employee_id: employeeId,
      from_email: storedUser.email,
      from_name: storedUser.name || "",
      leave_type: leaveForm.leaveType,
      to_email: leaveForm.to,
      cc_email: leaveForm.ccList.join(","),
      subject: leaveForm.subject === "__custom"
        ? leaveForm.customSubject
        : leaveForm.subject,
      body: leaveForm.body,
    };

    // Add additional fields based on leave type
    if (leaveForm.leaveType === "Planned Leave" || leaveForm.leaveType === "Unplanned Leave") {
      Object.assign(payload, {
        leave_date: leaveRecords.map(r => r.leaveDate),
        leave_records: leaveRecords.map(record => ({
          leaveDate: record.leaveDate,
          leaveDay: record.leaveDay,
          leaveType: record.leaveType
        }))
      });
    }
    else if (leaveForm.leaveType === "Work From Home Request") {
      Object.assign(payload, {
        leave_date: wfhRecords.map(r => r.wfhDate),
        leave_records: wfhRecords.map(record => ({
          wfhDate: record.wfhDate,
          wfhDay: record.wfhDay
        }))
      });
    }
    else if (leaveForm.leaveType === "Compensatory Off (Leave)") {
      Object.assign(payload, {
        comp_off_records: compOffRecords.map(record => ({
          workedDate: record.workedDate,
          duration: record.duration,
          compOffDate: record.compOffDate
        }))
      });
    }
    else if (leaveForm.leaveType === "Compensatory Off (Cash)") {
      Object.assign(payload, {
        cash_off_records: cashOffRecords.map(r => ({
          workedDate: r.workedDate,
          duration: r.duration
        }))
      });
    }
    else if (leaveForm.leaveType === "Travel (Site Visit)") {
      Object.assign(payload, {
        travel_records: travelRecords.map(record => ({
          location: record.location,
          fromDateTime: record.fromDateTime,
          toDateTime: record.toDateTime,
        }))
      });
    }
    else if (leaveForm.leaveType === "Attendance CI/CO Correction") {
      Object.assign(payload, {
        leave_date: [leaveForm.leaveDate] || []
      });
    }
    else {
      Object.assign(payload, {
        leave_date: [leaveForm.leaveDate] || [],
        duration: leaveForm.duration,
        workedDate: leaveForm.workedDate,
        comp_off_date: leaveForm.compOffDate
      });
    }

    try {
      const res = await axios.post(`${API_URL}/api/send-leave-email`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.status === 207) {
        toast.error(res.data.details.errors[0]);
      }
      if (res.status === 200) {
        showCustomToast("Email sent successfully!");

        // Reset form
        setLeaveForm({
          leaveType: "",
          leaveDate: [emailDate],
          to: "",
          ccList: ["tushar.mahadik@protovec.com", "kalpesh.urunkar@protovec.com"],
          subject: "",
          customSubject: "",
          body: "",
          compOffDate: "",
          duration: "",
          workedDate: ""
        });

        // Reset all date pickers and records
        setSelectedDates([]);
        setLeaveRecords([]);
        setWfhRecords([]);
        setCompOffRecords([]);
        setCashOffRecords([]);
        setCashOffDayInfo({});
        setTravelRecords([]);
        setShowDatePicker(false);

        loadMyRequests();
      }
    } catch (error) {
      console.log(error)
      // Handle partial success (207 status)
      if (error.response?.status === 207) {
        const { data } = error.response;

        // Create a custom toast for partial success
        const ToastContent = () => (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{
                background: '#f0ad4e',
                color: 'white',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px'
              }}>
                ⚠
              </span>
              <strong style={{ color: '#856404' }}>Partial Success</strong>
            </div>

            <p style={{ margin: '5px 0', fontSize: '0.9em' }}>
              {data.summary || `${data.details?.validRecords || 0} valid, ${data.details?.invalidRecords || 0} invalid`}
            </p>

            <div style={{
              maxHeight: '150px',
              overflowY: 'auto',
              background: '#f8f9fa',
              padding: '8px',
              borderRadius: '4px',
              margin: '8px 0'
            }}>
              <strong style={{ fontSize: '0.85em' }}>Validation Errors:</strong>
              <ul style={{ margin: '5px 0', paddingLeft: '20px', fontSize: '0.85em' }}>
                {data.details?.errors?.map((error, idx) => (
                  <li key={idx} style={{ marginBottom: '3px' }}>{error}</li>
                )) || data.errors?.map((error, idx) => (
                  <li key={idx} style={{ marginBottom: '3px' }}>{error}</li>
                ))}
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                onClick={() => {
                  // Filter out invalid records and auto-submit
                  if (data.details?.invalidRecords) {
                    const invalidSet = new Set(data.details.invalidRecords.map(ir =>
                      `${ir.record.workedDate}-${ir.record.compOffDate}`
                    ));

                    const updatedCompOffRecords = compOffRecords.filter(record =>
                      !invalidSet.has(`${record.workedDate}-${record.compOffDate}`)
                    );

                    setCompOffRecords(updatedCompOffRecords);

                    // Show success message
                    showCustomToast(`Filtered invalid records. ${updatedCompOffRecords.length} valid record(s) remain. Please submit again.`, {
                      type: 'info',
                      duration: 4000
                    });
                  }

                  // Close this toast
                  toast.dismiss();
                }}
                style={{
                  flex: 1,
                  background: '#f0ad4e',
                  color: 'white',
                  border: 'none',
                  padding: '8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85em'
                }}
              >
                Keep Valid & Retry
              </button>

              <button
                onClick={() => {
                  // Just close the toast and let user manually fix
                  toast.dismiss();

                  // Show info message
                  showCustomToast("Please fix the invalid records and try again.", {
                    type: 'info',
                    duration: 3000
                  });
                }}
                style={{
                  flex: 1,
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85em'
                }}
              >
                Fix Manually
              </button>
            </div>
          </div>
        );

        // Show the custom toast
        toast(ToastContent, {
          position: "top-center",
          autoClose: false, // Don't auto-close
          closeOnClick: false,
          draggable: true,
          style: {
            background: '#fff3cd',
            borderLeft: '5px solid #f0ad4e',
            color: '#856404',
            minWidth: '400px',
            maxWidth: '500px'
          }
        });

      } else if (error.response?.status === 400) {
        const data = error.response.data;
        const errors = data.errors;
        if (errors && Array.isArray(errors) && errors.length > 0) {
          errors.forEach(errMsg => toast.error(errMsg, { autoClose: 7000 }));
        } else {
          toast.error(data.message || "Validation failed. Please check your inputs.", { autoClose: 7000 });
        }
      } else {
        showCustomToast(error.response?.data?.message || "Failed to send email", {
          type: 'error'
        });
      }
    } finally {
      setIsSending(false);
      setShowConfirmModal(false);
    }
  }, [isSending, employeeId, storedUser, leaveForm, leaveRecords, wfhRecords, compOffRecords, cashOffRecords, travelRecords, API_URL, emailDate, showCustomToast, loadMyRequests]);

  const saveEditBody = useCallback(async () => {
    const tmpl = templates.find((t) => t.subject === leaveForm.subject);
    if (!tmpl) return;
    setEditingBody(false);
    try {
      const res = await fetch(`${API_URL}/api/subject-templates/${tmpl.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: editBody }),
      });
      if (!res.ok) throw new Error();
      showCustomToast("Template updated");
      loadTemplates();
      setLeaveForm((p) => ({ ...p, body: editBody }));
    } catch {
      showCustomToast("Failed to update template");
    }
  }, [templates, leaveForm.subject, API_URL, editBody, showCustomToast, loadTemplates]);

  const saveNewTemplate = useCallback(async () => {
    if (!newSubject.trim() || !newBody.trim()) return;
    setAddSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/subject-templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: newSubject.trim(),
          body: newBody.trim(),
        }),
      });
      if (!res.ok) throw new Error();
      showCustomToast("New template added");
      setShowAddModal(false);
      setNewSubject("");
      setNewBody("");
      loadTemplates();
    } catch {
      showCustomToast("Failed to add template");
    } finally {
      setAddSaving(false);
    }
  }, [newSubject, newBody, API_URL, showCustomToast, loadTemplates]);

  const commitSubjectEdit = useCallback(() => {
    const val = editSubject.trim();
    if (val) {
      setLeaveForm((p) => ({
        ...p,
        subject: "__custom",
        customSubject: val,
      }));
      setEditingSubject(false);
    }
  }, [editSubject]);

  const handleLeaveModal = useCallback((r) => {
    setLeaveData(r);
    showLeaveRequestModal(true);
  }, []);

  const closeHandleLeaveModal = useCallback(() => {
    showLeaveRequestModal(false);
    setLeaveData({});
  }, []);

  useEffect(() => {
    loadTemplates();
    if (employeeId) loadMyRequests();
  }, [employeeId, loadTemplates, loadMyRequests]);

  useEffect(() => {
    socketRef.current = io(SOCKET_URL);

    socketRef.current.on("newPendingRequest", (newReq) => {
      if (newReq.employee_id === employeeId) {
        setMyReqs((prev) => [newReq, ...(prev || [])]);
      }
    });

    socketRef.current.on("requestDecision", (payload) => {
      if (payload.employee_id !== employeeId) return;
      setMyReqs((prev) =>
        (prev || []).map((r) =>
          r.request_id === payload.requestId
            ? { ...r, status: payload.decision, created_at: payload.timestamp }
            : r
        )
      );
    });

    return () => {
      socketRef.current.disconnect();
    };
  }, [employeeId, SOCKET_URL]);

  const totalPages = Math.ceil((myReqs?.length || 0) / itemsPerPage);
  const paginatedReqs = (myReqs || []).slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateTotalDays = (records) => {
    const total = records.reduce((total, rec) => {
      if (!rec?.leaveDay) return total + 1;
      const dayValue = rec.leaveDay.toLowerCase() === "half" ? 0.5 : 1;
      return total + dayValue;
    }, 0);
    return total;
  };

  return (
    <div className="mail-request-container">
      <Row className="g-3">
        {/* Apply for Leave Card */}
        <Col lg={8}>
          <Card className="mail-card shadow border-0 h-100">
            <Card.Header className="mail-card-header bg-primary text-white d-flex align-items-center">
              <FaEnvelope className="me-2 small-icon" />
              <h5 className="mb-0 fw-bold" style={{ fontSize: '1.2rem' }}>Leave & Request Management</h5>
            </Card.Header>
            <Card.Body className="p-3">
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Row className="g-2 form-row-compact">
                  {/* Request Type & Email Date */}
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <div className="form-label-icon">
                        <FaUserTag className="small-icon" />
                        <Form.Label className="mb-0">Request Type</Form.Label>
                      </div>
                      <Form.Select
                        name="leaveType"
                        value={leaveForm.leaveType}
                        onChange={handleFormChange}
                        required
                        className="form-control-compact border-primary-subtle"
                      >
                        <option value="">-- Select Request Type --</option>
                        <option>Attendance CI/CO Correction</option>
                        <option>Planned Leave</option>
                        <option>Unplanned Leave</option>
                        <option>Work From Home Request</option>
                        <option>Compensatory Off (Leave)</option>
                        <option>Compensatory Off (Cash)</option>
                        <option>Travel (Site Visit)</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                        Please select a leave type.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <div className="form-label-icon">
                        <FaCalendarAlt className="small-icon" />
                        <Form.Label className="mb-0">Email Date</Form.Label>
                      </div>
                      <div>
                        <Form.Control
                          type="text"
                          name="leaveDate"
                          className="form-control-compact bg-light border-primary-subtle"
                          value={emailDate}
                          readOnly
                          disabled
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <div className="form-label-icon">
                        <FaUser className="small-icon" />
                        <Form.Label className="mb-0">To Email</Form.Label>
                      </div>
                      <Form.Control
                        type="email"
                        name="to"
                        value={leaveForm.to}
                        onChange={handleFormChange}
                        required
                        placeholder="recipient@company.com"
                        className="form-control-compact border-primary-subtle"
                      />
                      <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                        Please enter a valid email.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <div className="form-label-icon">
                        <FaCopy className="small-icon" />
                        <Form.Label className="mb-0">Add CC Email</Form.Label>
                      </div>
                      <InputGroup className="input-group-compact">
                        <Form.Control
                          type="email"
                          placeholder="Enter CC email"
                          value={newCc}
                          onChange={(e) => setNewCc(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleAddCc(e)}
                          className="form-control-compact border-primary-subtle"
                        />
                        <Button
                          variant="primary"
                          onClick={handleAddCc}
                          className="compact-button"
                        >
                          <FaPlus className="small-icon" />
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  {/* CC List Badges */}
                  <div className="mt-1">
                    <small className="text-muted mb-1 d-block" style={{ fontSize: '0.75rem' }}>
                      CC List ({leaveForm.ccList.length} emails)
                    </small>
                    <div>
                      {leaveForm.ccList.length > 0 ? (
                        leaveForm.ccList.map((c) => (
                          <div key={c} className="cc-badge">
                            <span className="text-truncate" style={{ maxWidth: '180px' }}>{c}</span>
                            <span
                              className="badge-close"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveCc(c);
                              }}
                              title="Remove CC"
                            >
                              <FaTimes size={10} />
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="cc-badges-empty">
                          No CC emails added
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date Picker Input Field for Planned/Unplanned Leave */}
                  {(leaveForm.leaveType === "Planned Leave" || leaveForm.leaveType === "Unplanned Leave") && (
                    <Col md={12}>
                      <Card className="border-primary bg-light mail-card mb-2">
                        <Card.Body className="p-2">
                          <h6 className="text-dark fw-bold d-flex align-items-center mb-2" style={{ fontSize: '0.85rem' }}>
                            <FaCalendar className="me-2 small-icon text-primary" />
                            Select Leave Dates
                          </h6>

                          {/* Multi Date Picker */}
                          <div className="mb-3">
                            <DatePicker
                              multiple
                              value={selectedDates}
                              onChange={handleDateSelection}
                              format="YYYY-MM-DD"
                              placeholder="Click to select dates"
                              inputClass="form-control-compact border-primary"
                              portal
                              style={{
                                width: '100%',
                                cursor: 'pointer'
                              }}
                              containerStyle={{
                                width: '100%'
                              }}
                              calendarPosition="bottom-center"
                            />
                          </div>

                          {/* Leave Records Table */}
                          {leaveRecords.length > 0 && (
                            <div className="mt-3">
                              <h6 className="text-dark fw-bold d-flex align-items-center mb-2 gap-3" style={{ fontSize: '0.85rem' }}>
                                <div>
                                  <FaClipboardList className="me-2 small-icon text-primary" />
                                  Leave Details ({calculateTotalDays(leaveRecords)} day{calculateTotalDays(leaveRecords) !== 1 ? 's' : ''})
                                </div>

                                {selectedDates.length > 0 && (
                                  <div className="align-items-center">
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedDates([]);
                                        setLeaveRecords([]);
                                      }}
                                      className="p-0"
                                      style={{ fontSize: '0.75rem' }}
                                    >
                                      Clear all
                                    </Button>
                                  </div>
                                )}
                              </h6>
                              <div className="table-responsive">
                                <table className="table table-sm table-bordered mb-2">
                                  <thead className="bg-light">
                                    <tr>
                                      <th style={{ fontSize: '0.8rem', width: '35%' }}>Date</th>
                                      <th style={{ fontSize: '0.8rem', width: '30%' }}>Type</th>
                                      <th style={{ fontSize: '0.8rem', width: '25%' }}>Duration</th>
                                      <th style={{ fontSize: '0.8rem', width: '10%' }}>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {leaveRecords.map((record, index) => (
                                      <tr key={index}>
                                        <td>
                                          <Form.Control
                                            type="date"
                                            disabled
                                            value={record.leaveDate}
                                            className="form-control-compact border-0 p-1"
                                            style={{ fontSize: '0.8rem' }}
                                          />
                                        </td>
                                        <td>
                                          <Form.Control
                                            value={leaveForm.leaveType === "Planned Leave" ? "Planned" :
                                              leaveForm.leaveType === "Unplanned Leave" ? "Unplanned" : ""}
                                            disabled
                                            className="form-control-compact border-0 p-1"
                                            style={{ fontSize: '0.8rem' }}
                                          />
                                        </td>
                                        <td>
                                          <Form.Select
                                            value={record.leaveDay}
                                            onChange={(e) => handleLeaveRecordChange(index, "leaveDay", e.target.value)}
                                            className="form-control-compact border-0 p-1"
                                            style={{ fontSize: '0.8rem' }}
                                          >
                                            <option value="Full">Full Day</option>
                                            <option value="Half">Half Day</option>
                                          </Form.Select>
                                        </td>
                                        <td className="text-center">
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeLeaveRecord(index)}
                                            className="compact-button p-1"
                                            title="Remove"
                                          >
                                            <FaMinus size={10} />
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  )}

                  {/* Work From Home Date Picker */}
                  {leaveForm.leaveType === "Work From Home Request" && (
                    <Col md={12}>
                      <Card className="border-info bg-light mail-card mb-2">
                        <Card.Body className="p-2">
                          <h6 className="text-dark fw-bold d-flex align-items-center mb-2" style={{ fontSize: '0.85rem' }}>
                            <FaHome className="me-2 small-icon text-info" />
                            Select Work From Home Dates
                          </h6>

                          {/* Multi Date Picker for WFH */}
                          <div className="mb-3">
                            <DatePicker
                              multiple
                              value={selectedDates}
                              onChange={handleDateSelection}
                              format="YYYY-MM-DD"
                              placeholder="Click to select dates"
                              inputClass="form-control-compact border-info"
                              portal
                              style={{
                                width: '100%',
                                cursor: 'pointer'
                              }}
                              containerStyle={{
                                width: '100%'
                              }}
                              calendarPosition="bottom-center"
                            />
                          </div>

                          {/* WFH Records Table */}
                          {wfhRecords.length > 0 && (
                            <div className="mt-3">
                              <h6 className="text-dark fw-bold d-flex align-items-center mb-2 gap-3" style={{ fontSize: '0.85rem' }}>
                                <div>
                                  <FaClipboardList className="me-2 small-icon text-info" />
                                  WFH Details ({calculateTotalWfhDays(wfhRecords)} day{calculateTotalWfhDays(wfhRecords) !== 1 ? 's' : ''})
                                </div>

                                {selectedDates.length > 0 && (
                                  <div className="align-items-center">
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedDates([]);
                                        setWfhRecords([]);
                                      }}
                                      className="p-0"
                                      style={{ fontSize: '0.75rem' }}
                                    >
                                      Clear all
                                    </Button>
                                  </div>
                                )}
                              </h6>
                              <div className="table-responsive">
                                <table className="table table-sm table-bordered mb-2">
                                  <thead className="bg-light">
                                    <tr>
                                      <th style={{ fontSize: '0.8rem', width: '45%' }}>Date</th>
                                      <th style={{ fontSize: '0.8rem', width: '45%' }}>Duration</th>
                                      <th style={{ fontSize: '0.8rem', width: '10%' }}>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {wfhRecords.map((record, index) => (
                                      <tr key={index}>
                                        <td>
                                          <Form.Control
                                            type="date"
                                            disabled
                                            value={record.wfhDate}
                                            className="form-control-compact border-0 p-1"
                                            style={{ fontSize: '0.8rem' }}
                                          />
                                        </td>
                                        <td>
                                          <Form.Select
                                            value={record.wfhDay}
                                            onChange={(e) => handleWfhRecordChange(index, "wfhDay", e.target.value)}
                                            className="form-control-compact border-0 p-1"
                                            style={{ fontSize: '0.8rem' }}
                                          >
                                            <option value="Full">Full Day</option>
                                            <option value="Half">Half Day</option>
                                          </Form.Select>
                                        </td>
                                        <td className="text-center">
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeWfhRecord(index)}
                                            className="compact-button p-1"
                                            title="Remove"
                                          >
                                            <FaMinus size={10} />
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  )}

                  {/* Travel Site Visit Section */}
                  {leaveForm.leaveType === "Travel (Site Visit)" && (
                    <Col md={12}>
                      <Card className="border-warning bg-light mail-card mb-2">
                        <Card.Body className="p-2">
                          <h6 className="text-dark fw-bold d-flex align-items-center mb-2" style={{ fontSize: '0.85rem' }}>
                            <FaRoute className="me-2 small-icon text-warning" />
                            Travel Site Visit Details
                          </h6>

                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0" style={{ fontSize: '0.85rem' }}>
                              Travel Records ({calculateTotalTravelDays(travelRecords)} total days)
                            </h6>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              onClick={addTravelRecord}
                              className="compact-button"
                            >
                              <FaPlus className="me-1" /> Add Record
                            </Button>
                          </div>

                          {travelRecords.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered mb-0">
                                <thead className="bg-light">
                                  <tr>
                                    <th style={{ fontSize: '0.8rem', width: '30%' }}>Location</th>
                                    <th style={{ fontSize: '0.8rem', width: '30%' }}>From (Date & Time)</th>
                                    <th style={{ fontSize: '0.8rem', width: '30%' }}>To (Date & Time)</th>
                                    <th style={{ fontSize: '0.8rem', width: '10%' }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {travelRecords.map((record, index) => (
                                    <React.Fragment key={index}>
                                      <tr>
                                        <td>
                                          <Form.Control
                                            type="text"
                                            value={record.location}
                                            onChange={(e) => updateTravelRecord(index, 'location', e.target.value)}
                                            placeholder="Enter location"
                                            required
                                            className="form-control-compact border-warning p-1"
                                            style={{ fontSize: '0.8rem' }}
                                          />
                                        </td>
                                        <td>
                                          <Form.Control
                                            type="datetime-local"
                                            value={record.fromDateTime}
                                            onChange={(e) => updateTravelRecord(index, 'fromDateTime', e.target.value)}
                                            required
                                            className="form-control-compact border-warning p-1"
                                            style={{ fontSize: '0.8rem' }}
                                          />
                                        </td>
                                        <td>
                                          <Form.Control
                                            type="datetime-local"
                                            value={record.toDateTime}
                                            onChange={(e) => updateTravelRecord(index, 'toDateTime', e.target.value)}
                                            required
                                            className="form-control-compact border-warning p-1"
                                            style={{ fontSize: '0.8rem' }}
                                          />
                                        </td>
                                        <td className="text-center">
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeTravelRecord(index)}
                                            className="compact-button p-1"
                                            title="Remove"
                                          >
                                            <FaTimes size={10} />
                                          </Button>
                                        </td>
                                      </tr>
                                      {record.error && (
                                        <tr>
                                          <td colSpan="4" className="p-1">
                                            <Alert variant="danger" className="py-1 mb-0 alert-compact">
                                              <FaTimes className="me-2 small-icon" />
                                              <span style={{ fontSize: '0.75rem' }}>{record.error}</span>
                                            </Alert>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <Alert variant="info" className="py-2 alert-compact">
                              <FaInfoCircle className="me-2" />
                              <span style={{ fontSize: '0.8rem' }}>
                                Click "Add Record" to add travel details. You can add multiple records for different site visits.
                              </span>
                            </Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  )}

                  {/* Comp Off Fields */}
                  {(leaveForm.leaveType === "Compensatory Off (Leave)" ||
                    leaveForm.leaveType === "Compensatory Off (Cash)") && (
                      <Col md={12}>
                        <Card className="border-secondary bg-light mail-card mb-2">
                          <Card.Body className="p-2">
                            <h6 className="text-dark fw-bold d-flex align-items-center mb-2" style={{ fontSize: '0.85rem' }}>
                              <FaClock className="me-2 small-icon text-secondary" />
                              Compensatory Off Details
                            </h6>

                            {leaveForm.leaveType === "Compensatory Off (Cash)" && (
                              <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6 className="mb-0" style={{ fontSize: '0.85rem' }}>
                                    Comp Off Records ({calculateTotalCashOffDays(cashOffRecords)} total days)
                                  </h6>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={addCashOffRecord}
                                    className="compact-button"
                                  >
                                    <FaPlus className="me-1" /> Add Record
                                  </Button>
                                </div>

                                {cashOffRecords.length > 0 ? (
                                  <div className="table-responsive">
                                    <table className="table table-sm table-bordered mb-0">
                                      <thead className="bg-light">
                                        <tr>
                                          <th style={{ fontSize: '0.8rem', width: '55%' }}>Worked On (Holiday / Weekend)</th>
                                          <th style={{ fontSize: '0.8rem', width: '35%' }}>Duration</th>
                                          <th style={{ fontSize: '0.8rem', width: '10%' }}>Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {cashOffRecords.map((record, index) => (
                                          <React.Fragment key={index}>
                                            <tr>
                                              <td>
                                                <Form.Control
                                                  type="date"
                                                  value={record.workedDate}
                                                  onChange={(e) => updateCashOffRecord(index, 'workedDate', e.target.value)}
                                                  required
                                                  max={new Date().toISOString().split('T')[0]}
                                                  className="form-control-compact border-secondary p-1"
                                                  style={{ fontSize: '0.8rem' }}
                                                />
                                                {cashOffDayInfo[index] && (() => {
                                                  const info = cashOffDayInfo[index];
                                                  console.log('info',info)
                                                  if (!info.hasWorked) return (
                                                    <div style={{ fontSize: '0.7rem', color: '#dc3545', marginTop: 2 }}>
                                                      ✗ No attendance found
                                                    </div>
                                                  );
                                                  const hrs = info.work_hour ? info.work_hour.toFixed(1) : '0';
                                                  const tag = info.isTravel
                                                    ? { label: `✈ Travel · ${info.location || ''}`, bg: '#dbeafe', color: '#1d4ed8' }
                                                    : { label: `🏢 Office`, bg: '#dcfce7', color: '#166534' };
                                                  return (
                                                    <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                      <span style={{ fontSize: '0.68rem', background: tag.bg, color: tag.color, borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                                                        {tag.label}
                                                      </span>
                                                      <span style={{ fontSize: '0.68rem', background: '#f3f4f6', color: '#374151', borderRadius: 4, padding: '1px 6px' }}>
                                                        {hrs}h worked
                                                      </span>
                                                    </div>
                                                  );
                                                })()}
                                              </td>
                                              <td>
                                                <Form.Select
                                                  value={record.duration}
                                                  onChange={(e) => updateCashOffRecord(index, 'duration', e.target.value)}
                                                  required
                                                  className="form-control-compact border-secondary p-1"
                                                  style={{ fontSize: '0.8rem', borderColor: cashOffDayInfo[index]?.suggested && !record.durationManual ? '#0d6efd' : undefined }}
                                                >
                                                  <option value="">Select Duration</option>
                                                  <option value="Full Day">Full Day</option>
                                                  <option value="Half Day">Half Day</option>
                                                </Form.Select>
                                                {cashOffDayInfo[index]?.suggested && (
                                                  <div style={{ fontSize: '0.68rem', color: '#0d6efd', marginTop: 2 }}>
                                                    ↑ Suggested: {cashOffDayInfo[index].suggested}
                                                  </div>
                                                )}
                                              </td>
                                              <td className="text-center">
                                                <Button
                                                  variant="outline-danger"
                                                  size="sm"
                                                  onClick={() => removeCashOffRecord(index)}
                                                  className="compact-button p-1"
                                                  title="Remove"
                                                >
                                                  <FaTimes size={10} />
                                                </Button>
                                              </td>
                                            </tr>
                                          </React.Fragment>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <Alert variant="info" className="py-2 alert-compact">
                                    <FaDollarSign className="me-2" />
                                    <span style={{ fontSize: '0.8rem' }}>
                                      Click "Add Record" to add comp off details. You can add multiple records for different worked dates.
                                    </span>
                                  </Alert>
                                )}
                              </>
                            )}

                            {leaveForm.leaveType === "Compensatory Off (Leave)" && (
                              <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <h6 className="mb-0" style={{ fontSize: '0.85rem' }}>
                                    Comp Off Records ({calculateTotalCompOffDays(compOffRecords)} total days)
                                  </h6>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={addCompOffRecord}
                                    className="compact-button"
                                  >
                                    <FaPlus className="me-1" /> Add Record
                                  </Button>
                                </div>

                                {compOffRecords.length > 0 ? (
                                  <div className="table-responsive">
                                    <table className="table table-sm table-bordered mb-0">
                                      <thead className="bg-light">
                                        <tr>
                                          <th style={{ fontSize: '0.8rem', width: '25%' }}>Worked On</th>
                                          <th style={{ fontSize: '0.8rem', width: '25%' }}>Duration Worked</th>
                                          <th style={{ fontSize: '0.8rem', width: '25%' }}>Comp Off Date</th>
                                          {/* <th style={{ fontSize: '0.8rem', width: '15%' }}>Status</th> */}
                                          <th style={{ fontSize: '0.8rem', width: '10%' }}>Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {compOffRecords.map((record, index) => (
                                          <React.Fragment key={index}>
                                            <tr>
                                              <td>
                                                <Form.Control
                                                  type="date"
                                                  value={record.workedDate}
                                                  onChange={(e) => updateCompOffRecord(index, 'workedDate', e.target.value)}
                                                  required
                                                  max={new Date().toISOString().split('T')[0]}
                                                  className="form-control-compact border-secondary p-1"
                                                  style={{ fontSize: '0.8rem' }}
                                                />
                                                {/* Travel / office info pill */}
                                                {compOffDayInfo[index] && (() => {
                                                  const info = compOffDayInfo[index];
                                                  console.log('info',info)
                                                  if (!info.hasWorked) return (
                                                    <div style={{ fontSize: '0.7rem', color: '#dc3545', marginTop: 2 }}>
                                                      ✗ No attendance found
                                                    </div>
                                                  );
                                                  const hrs = info.work_hour ? info.work_hour.toFixed(1) : '0';
                                                  const tag = info.isTravel
                                                    ? { label: `✈ Travel · ${info.location || ''}`, bg: '#dbeafe', color: '#1d4ed8' }
                                                    : { label: `🏢 Office`, bg: '#dcfce7', color: '#166534' };
                                                  return (
                                                    <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                      <span style={{ fontSize: '0.68rem', background: tag.bg, color: tag.color, borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>
                                                        {tag.label}
                                                      </span>
                                                      <span style={{ fontSize: '0.68rem', background: '#f3f4f6', color: '#374151', borderRadius: 4, padding: '1px 6px' }}>
                                                        {hrs}h worked
                                                      </span>
                                                    </div>
                                                  );
                                                })()}
                                              </td>
                                              <td>
                                                <Form.Select
                                                  value={record.duration}
                                                  onChange={(e) => updateCompOffRecord(index, 'duration', e.target.value)}
                                                  required
                                                  className="form-control-compact border-secondary p-1"
                                                  style={{ fontSize: '0.8rem', borderColor: compOffDayInfo[index]?.suggested && !record.durationManual ? '#0d6efd' : undefined }}
                                                >
                                                  <option value="">Select Duration</option>
                                                  <option value="Full Day">Full Day</option>
                                                  <option value="Half Day">Half Day</option>
                                                </Form.Select>
                                                {/* Suggestion label */}
                                                {compOffDayInfo[index]?.suggested && (
                                                  <div style={{ fontSize: '0.68rem', color: '#0d6efd', marginTop: 2 }}>
                                                    ↑ Suggested: {compOffDayInfo[index].suggested}
                                                  </div>
                                                )}
                                                {compOffDayInfo[index]?.hasWorked === false ? null :
                                                  compOffDayInfo[index] && !compOffDayInfo[index].suggested && (
                                                    <div style={{ fontSize: '0.68rem', color: '#dc3545', marginTop: 2 }}>
                                                      &lt;5h — not eligible
                                                    </div>
                                                  )
                                                }
                                              </td>
                                              <td>
                                                <Form.Control
                                                  type="date"
                                                  value={record.compOffDate}
                                                  onChange={(e) => updateCompOffRecord(index, 'compOffDate', e.target.value)}
                                                  required
                                                  className="form-control-compact border-secondary p-1"
                                                  style={{ fontSize: '0.8rem' }}
                                                />
                                              </td>

                                              <td className="text-center">
                                                <Button
                                                  variant="outline-danger"
                                                  size="sm"
                                                  onClick={() => removeCompOffRecord(index)}
                                                  className="compact-button p-1"
                                                  title="Remove"
                                                >
                                                  <FaTimes size={10} />
                                                </Button>
                                              </td>
                                            </tr>
                                            {record.error && (
                                              <tr>
                                                <td colSpan="5" className="p-1">
                                                  <Alert variant="danger" className="py-1 mb-0 alert-compact">
                                                    <FaTimes className="me-2 small-icon" />
                                                    <span style={{ fontSize: '0.75rem' }}>{record.error}</span>
                                                  </Alert>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : (
                                  <Alert variant="info" className="py-2 alert-compact">
                                    <FaInfoCircle className="me-2" />
                                    <span style={{ fontSize: '0.8rem' }}>
                                      Click "Add Record" to add comp off details. You can add multiple records for different worked dates.
                                    </span>
                                  </Alert>
                                )}
                              </>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    )}

                  {/* Subject */}
                  <Col md={12}>
                    <Form.Group className="mb-2">
                      <div className="form-label-icon">
                        <FaFileAlt className="small-icon" />
                        <Form.Label className="mb-0">Email Subject</Form.Label>
                      </div>
                      {editingSubject ? (
                        <Form.Control
                          type="text"
                          value={editSubject}
                          autoFocus
                          onChange={(e) => setEditSubject(e.target.value)}
                          onBlur={commitSubjectEdit}
                          onKeyDown={(e) => e.key === "Enter" && commitSubjectEdit()}
                          required
                          className="form-control-compact border-primary-subtle"
                        />
                      ) : (
                        <InputGroup
                          onDoubleClick={() => {
                            setEditingSubject(true);
                            setEditSubject(
                              leaveForm.subject === "__custom"
                                ? leaveForm.customSubject
                                : leaveForm.subject
                            );
                          }}
                          className="input-group-compact"
                        >
                          <Form.Select
                            name="subject"
                            value={leaveForm.subject}
                            onChange={handleFormChange}
                            required
                            className="form-control-compact border-primary-subtle"
                          >
                            <option key="default" value="">
                              -- Select Subject Template --
                            </option>
                            {templates.map((t) => (
                              <option key={t.id} value={t.subject}>
                                {t.subject}
                              </option>
                            ))}
                            <option key="__custom" value="__custom">
                              Custom Subject
                            </option>
                          </Form.Select>
                          {isAdmin && (
                            <>
                              {leaveForm.subject && leaveForm.subject !== "__custom" && (
                                <Button
                                  variant="outline-primary"
                                  onClick={() => {
                                    setEditingBody((e) => !e);
                                    setEditBody(leaveForm.body);
                                  }}
                                  title="Edit Template"
                                  className="compact-button"
                                >
                                  <FaEdit className="small-icon" />
                                </Button>
                              )}
                              <Button
                                variant="outline-success"
                                onClick={() => setShowAddModal(true)}
                                title="Add New Template"
                                className="compact-button"
                              >
                                <FaPlus className="small-icon" />
                              </Button>
                            </>
                          )}
                        </InputGroup>
                      )}

                      {leaveForm.subject === "__custom" && !editingSubject && (
                        <Form.Control
                          className="mt-1 form-control-compact border-primary-subtle"
                          type="text"
                          placeholder="Enter custom subject"
                          value={leaveForm.customSubject}
                          onChange={handleFormChange}
                          name="customSubject"
                          required
                        />
                      )}
                    </Form.Group>
                  </Col>

                  {/* Body */}
                  <Col md={12}>
                    <Form.Group className="mb-2">
                      <div className="form-label-icon">
                        <FaEnvelope className="small-icon" />
                        <Form.Label className="mb-0">Email Body</Form.Label>
                      </div>
                      {editingBody ? (
                        <>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={editBody}
                            onChange={(e) => setEditBody(e.target.value)}
                            className="form-control-compact border-primary-subtle"
                          />
                          <div className="mt-2 d-flex gap-2">
                            <Button
                              variant="success"
                              onClick={saveEditBody}
                              className="flex-grow-1 compact-button"
                            >
                              <FaSave className="me-1 small-icon" /> Save
                            </Button>
                            <Button
                              variant="outline-secondary"
                              onClick={() => {
                                setEditingBody(false);
                                setEditBody(leaveForm.body);
                              }}
                              className="flex-grow-1 compact-button"
                            >
                              <FaUndo className="me-1 small-icon" /> Cancel
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <Form.Control
                            as="textarea"
                            name="body"
                            rows={10}
                            value={leaveForm.body}
                            onChange={handleFormChange}
                            required
                            className="form-control-compact border-primary-subtle"
                            placeholder="Write your email body here..."
                          />
                          <div className="d-flex justify-content-between mt-1">
                            <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {leaveForm.body.trim().split(/\s+/).filter(Boolean).length}
                              /500 words
                            </Form.Text>
                            <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {500 - leaveForm.body.trim().split(/\s+/).filter(Boolean).length} words remaining
                            </Form.Text>
                          </div>
                        </>
                      )}
                    </Form.Group>
                  </Col>

                  {/* Submit Button */}
                  <Col md={12}>
                    <div className="d-flex justify-content-end mt-2">
                      <Button
                        type="submit"
                        disabled={isSending}
                        variant="primary"
                        className="px-4 compact-button"
                        style={{ minHeight: '36px' }}
                      >
                        {isSending ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <FaEnvelope className="me-2" />
                            Send Request
                          </>
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* My Requests Card - Compact sidebar */}
        <Col lg={4}>
          <Card className="mail-card shadow border-0" style={{ overflowX: 'hidden' }}>
            <Card.Header className="mail-card-header bg-success text-white d-flex align-items-center justify-content-between py-2">
              <div className="d-flex align-items-center" style={{ maxWidth: '80%' }}>
                <FaHistory className="me-2 small-icon flex-shrink-0" />
                <h6 className="mb-0 fw-bold" style={{ fontSize: '1.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  My Recent Requests
                </h6>
              </div>
              <Badge bg="light" text="dark" className="compact-badge fw-normal flex-shrink-0">
                {myReqs?.length || 0} total
              </Badge>
            </Card.Header>

            <Card.Body className="p-2" style={{ overflowX: 'hidden' }}>
              {loadingReqs ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" variant="success" />
                  <p className="text-muted small mt-1">Loading...</p>
                </div>
              ) : errorReqs ? (
                <Alert variant="danger" className="py-1 mb-1 alert-compact" style={{ wordBreak: 'break-word' }}>
                  <small>{errorReqs}</small>
                </Alert>
              ) : (myReqs || []).length === 0 ? (
                <div className="text-center py-3">
                  <FaEnvelope className="text-muted mb-2" size={28} />
                  <p className="text-muted small mb-0">No requests yet</p>
                  <p className="text-muted small">Your requests will appear here</p>
                </div>
              ) : (
                <div className="requests-list" style={{ overflowX: 'hidden' }}>
                  {paginatedReqs.map((r, idx) => (
                    <div
                      key={`${r.request_id}-${idx}`}
                      className="request-item"
                      onClick={() => handleLeaveModal(r)}
                      style={{ wordBreak: 'break-word' }}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
                          <div className="d-flex align-items-center mb-1">
                            <div className="me-1 flex-shrink-0">
                              <Badge
                                bg={
                                  r.status === "approved"
                                    ? "success"
                                    : r.status === "pending"
                                      ? "warning"
                                      : "danger"
                                }
                                className="status-badge"
                              >
                                {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                              </Badge>
                            </div>
                            <small className="text-muted flex-shrink-0" style={{ fontSize: '0.8rem', color: '#000', fontWeight: "bold" }}>
                              {formatDate(r.created_at)}
                            </small>
                          </div>
                          <p className="mb-1 fw-medium small" style={{
                            lineHeight: '1.2',
                            fontSize: '1rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {r.subject}
                          </p>
                          {r.leave_type && (
                            <small className="text-muted d-block" style={{
                              fontSize: '0.8rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              <FaUserTag size={9} className="me-1 flex-shrink-0" />
                              {r.leave_type}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>

            {/* Footer with flex-wrap */}
            {myReqs?.length > 0 && (
              <Card.Footer className="bg-light py-1">
                <div className="d-flex justify-content-between align-items-center small flex-wrap" style={{ fontSize: '0.75rem', gap: '0.5rem' }}>
                  <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                    <div className="d-flex align-items-center">
                      <Badge bg="success" className="compact-badge me-1">
                        {myReqs.filter(r => r.status === 'approved').length}
                      </Badge>
                      <span className="text-muted">Approved</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <Badge bg="warning" className="compact-badge me-1">
                        {myReqs.filter(r => r.status === 'pending').length}
                      </Badge>
                      <span className="text-muted">Pending</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <Badge bg="danger" className="compact-badge me-1">
                        {myReqs.filter(r => r.status === 'rejected').length}
                      </Badge>
                      <span className="text-muted">Rejected</span>
                    </div>
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="d-flex align-items-center" style={{ gap: '0.25rem' }}>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', lineHeight: '1' }}
                      >
                        <FaChevronLeft size={10} />
                      </Button>
                      <span className="px-2 text-muted" style={{ fontSize: '0.7rem' }}>
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', lineHeight: '1' }}
                      >
                        <FaChevronRight size={10} />
                      </Button>
                    </div>
                  )}
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        className="modal-compact"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <FaEnvelope className="me-2 small-icon" />
            <span style={{ fontSize: '0.95rem' }}>{
                leaveForm.subject === "__custom"
                  ? leaveForm.customSubject
                  : leaveForm.subject
              }</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="d-flex align-items-center mb-2 alert-compact">
            <FaCheckCircle className="me-2 small-icon" />
            <span style={{ fontSize: '0.85rem' }}>Please review your request before sending.</span>
          </Alert>

          <div className="compact-preview-section">
            <h6 className="d-flex align-items-center">
              <FaUser className="me-2 small-icon text-primary" />
              <span>Recipients</span>
            </h6>
            <Row>
              <Col md={6}>
                <p><strong>To:</strong> {leaveForm.to}</p>
              </Col>
              <Col md={6}>
                <p><strong>CC:</strong> {leaveForm.ccList.join(", ")}</p>
              </Col>
            </Row>
          </div>

          {leaveForm.leaveType && (
            <div className="compact-preview-section">
              <h6 className="d-flex align-items-center">
                <FaClipboardList className="me-2 small-icon text-primary" />
                <span>Request Details</span>
              </h6>
              <p><strong>Type:</strong> {leaveForm.leaveType}</p>

              {/* Show leave records if Planned/Unplanned */}
              {(leaveForm.leaveType === "Planned Leave" || leaveForm.leaveType === "Unplanned Leave") && leaveRecords.length > 0 && (
                <div className="mt-2">
                  <p><strong>Leave Dates:</strong></p>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ fontSize: '0.8rem' }}>Date</th>
                          <th style={{ fontSize: '0.8rem' }}>Type</th>
                          <th style={{ fontSize: '0.8rem' }}>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaveRecords.map((record, index) => (
                          <tr key={index}>
                            <td style={{ fontSize: '0.8rem' }}>{record.leaveDate}</td>
                            <td style={{ fontSize: '0.8rem' }}>{record.leaveType}</td>
                            <td style={{ fontSize: '0.8rem' }}>{record.leaveDay} Day</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Total: {calculateTotalDays(leaveRecords)} day(s)
                  </small>
                </div>
              )}

              {/* Show WFH records if Work From Home */}
              {leaveForm.leaveType === "Work From Home Request" && wfhRecords.length > 0 && (
                <div className="mt-2">
                  <p><strong>Work From Home Dates:</strong></p>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ fontSize: '0.8rem' }}>Date</th>
                          <th style={{ fontSize: '0.8rem' }}>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wfhRecords.map((record, index) => (
                          <tr key={index}>
                            <td style={{ fontSize: '0.8rem' }}>{record.wfhDate}</td>
                            <td style={{ fontSize: '0.8rem' }}>{record.wfhDay} Day</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Total: {calculateTotalWfhDays(wfhRecords)} day(s)
                  </small>
                </div>
              )}

              {/* Show Travel Site Visit records */}
              {leaveForm.leaveType === "Travel (Site Visit)" && travelRecords.length > 0 && (
                <div className="mt-2">
                  <p><strong>Travel Site Visit Details:</strong></p>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ fontSize: '0.8rem' }}>Location</th>
                          <th style={{ fontSize: '0.8rem' }}>From</th>
                          <th style={{ fontSize: '0.8rem' }}>To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {travelRecords.map((record, index) => (
                          <tr key={index}>
                            <td style={{ fontSize: '0.8rem' }}>{record.location}</td>
                            <td style={{ fontSize: '0.8rem' }}>{record.fromDateTime ? new Date(record.fromDateTime).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : ''}</td>
                            <td style={{ fontSize: '0.8rem' }}>{record.toDateTime ? new Date(record.toDateTime).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Total: {calculateTotalTravelDays(travelRecords)} day(s)
                  </small>
                </div>
              )}

              {/* Show Comp Off records if Compensatory Off (Leave) */}
              {leaveForm.leaveType === "Compensatory Off (Leave)" && compOffRecords.length > 0 && (
                <div className="mt-2">
                  <p><strong>Compensatory Off Details:</strong></p>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ fontSize: '0.8rem' }}>Worked On</th>
                          <th style={{ fontSize: '0.8rem' }}>Duration Worked</th>
                          <th style={{ fontSize: '0.8rem' }}>Comp Off Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compOffRecords.map((record, index) => (
                          <tr key={index}>
                            <td style={{ fontSize: '0.8rem' }}>{record.workedDate}</td>
                            <td style={{ fontSize: '0.8rem' }}>{record.duration}</td>
                            <td style={{ fontSize: '0.8rem' }}>{record.compOffDate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Total: {calculateTotalCompOffDays(compOffRecords)} day(s)
                  </small>
                </div>
              )}

              {/* Show Cash Comp Off */}
              {leaveForm.leaveType === "Compensatory Off (Cash)" && cashOffRecords.length > 0 && (
                <div className="mt-2">
                  <p><strong>Compensatory Off (Cash) Details:</strong></p>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ fontSize: '0.8rem' }}>Worked On</th>
                          <th style={{ fontSize: '0.8rem' }}>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashOffRecords.map((record, index) => (
                          <tr key={index}>
                            <td style={{ fontSize: '0.8rem' }}>{record.workedDate}</td>
                            <td style={{ fontSize: '0.8rem' }}>{record.duration}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Total: {calculateTotalCashOffDays(cashOffRecords)} day(s) — will be processed as monetary compensation.
                  </small>
                </div>
              )}
            </div>
          )}

          <div>
          
              <div className="email-preview-body">
                {leaveForm.body}
              </div>
            <Alert variant="warning" className="mb-0 py-1 alert-compact">
              <FaTimes className="me-2 small-icon" />
              <span style={{ fontSize: '0.85rem' }}>Are you sure you want to send this email?</span>
            </Alert>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowConfirmModal(false)}
            className="compact-button"
          >
            <FaTimes className="me-1 small-icon" /> Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmSend}
            disabled={isSending}
            className="compact-button"
          >
            {isSending ? (
              <>
                <Spinner size="sm" animation="border" className="me-1" />
                Sending...
              </>
            ) : (
              <>
                <FaEnvelope className="me-1 small-icon" />
                Send Email
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add-Template Modal */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        className="modal-compact"
      >
        <Modal.Header closeButton className="bg-success">
          <Modal.Title className="d-flex align-items-center">
            <FaPlus className="me-2 small-icon" />
            <span style={{ fontSize: '0.95rem' }}>Add New Template</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
              <FaFileAlt className="me-2 small-icon text-success" />
              Template Subject
            </Form.Label>
            <Form.Control
              type="text"
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder="e.g., Planned Leave Request"
              className="form-control-compact border-success-subtle"
              autoFocus
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="fw-semibold d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
              <FaEnvelope className="me-2 small-icon text-success" />
              Template Body
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              placeholder="Enter the email body template..."
              className="form-control-compact border-success-subtle"
            />
            <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
              <FaInfoCircle className="me-1 small-icon" />
              This template will be available for all users.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAddModal(false)} className="compact-button">
            <FaTimes className="me-1 small-icon" /> Cancel
          </Button>
          <Button
            variant="success"
            onClick={saveNewTemplate}
            disabled={addSaving || !newSubject.trim() || !newBody.trim()}
            className="compact-button"
          >
            {addSaving ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <FaSave className="me-1 small-icon" />
                Save Template
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Notification */}
      {showToast && (
        <div className="custom-toast fade-in">
          <FaCheckCircle className="me-2" style={{ color: "#28a745", fontSize: 16 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, color: "#333", fontSize: '0.85rem' }}>Success!</div>
            <div style={{ fontSize: '0.8rem', color: "#666" }}>{toastMsg}</div>
          </div>
          <button
            onClick={() => setShowToast(false)}
            className="border-0 bg-transparent text-secondary"
            style={{ cursor: "pointer", fontSize: 14, padding: 2, marginLeft: 8 }}
            aria-label="Close toast"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Leave Request Modal */}
      {leaveRequestModal && (
        <LeaveRequestsModal
          show={leaveRequestModal}
          handleClose={closeHandleLeaveModal}
          leaveRequest={leaveData}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default MailRequest;